// The booking rate in Blueprint (BL-003, BL-189): a part of Inbox › Calls, opened from its head. Over a
// range of the company's days, by who took the calls, by reason or by campaign: a CSR sees their own;
// owners and managers see everyone's, with how many of each person's reasons were changed in review.
// A Section of RowList rows: each its name, its rate at its end, its leads and counts under it.
import { useState } from 'react';
import { RowList, SelectField, type Row } from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { rangeOf, rateLine, ratePct, takersNote, type RangeKey, type RateRow } from './reasons-model.ts';
import {
  BYS,
  changedWord,
  countsLine,
  missedLine,
  RANGES,
  useBookingRate,
  type By,
} from './use-booking-rate.ts';

/** One row of the rate: a person, a reason or a campaign (or everyone's, first). */
export function rateRow(x: RateRow, by: By, i: number): Row {
  const changed = changedWord(x);
  return {
    id: `${x.key ?? 'none'}-${x.label}-${i}`,
    name: x.label,
    ...(changed ? { word: { text: changed, tone: 'muted' as const } } : {}),
    lines: [
      {
        text: by === 'reason' ? `${x.taken} ${x.taken === 1 ? 'call' : 'calls'}` : rateLine(x),
        tone: 'ink' as const,
      },
      { text: countsLine(x) },
    ],
    ...(by !== 'reason' ? { end: { figure: ratePct(x.rate) } } : {}),
  };
}

export function BookingRateSection({ today, everyone }: { today: string; everyone: boolean }) {
  const [range, setRange] = useState<RangeKey>('week');
  const [by, setBy] = useState<By>('csr');
  const { r, error, retry } = useBookingRate(range, by, today);
  const rows = r ? (everyone && r.rows.length ? [r.total, ...r.rows] : r.rows) : [];
  const takers = r && by === 'csr' ? takersNote(rangeOf(range, today).from, r.takersSince, r.rows) : null;
  const missed = r && everyone ? missedLine(r.missed) : null;
  return (
    <Section
      title="Booking rate"
      status={error ? 'failed' : r ? 'ready' : 'loading'}
      what="the booking rate"
      failedText={error ?? ''}
      onRetry={retry}
    >
      <div className="calls-bp-rate">
        <div className="calls-bp-rate-picks">
          <SelectField
            label="When"
            width="fit"
            value={range}
            onChange={(v) => setRange(v as RangeKey)}
            options={RANGES.map(([value, label]) => ({ value, label }))}
          />
          {everyone && (
            <SelectField
              label="Show"
              width="fit"
              value={by}
              onChange={(v) => setBy(v as By)}
              options={BYS.map(([value, label]) => ({ value, label }))}
            />
          )}
        </div>
        <p className="calls-bp-muted">
          Booked ÷ the leads that count: a call under a minute, or with an excused reason (after hours, say),
          doesn’t count against whoever took it.
        </p>
        <RowList
          label="Booking rate"
          rows={rows.map((x, i) => rateRow(x, by, i))}
          empty="No calls taken in this range."
          {...(missed || takers
            ? {
                note: (
                  <>
                    {missed}
                    {missed && takers ? ' ' : null}
                    {takers}
                  </>
                ),
              }
            : {})}
        />
      </div>
    </Section>
  );
}
